import { useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '@/api/client';
import { Upload, Check, X, Download, FileText, Camera } from 'lucide-react';

interface Doc {
  id: number;
  candidato_id: number;
  codigo: string;
  bloque: 'A' | 'B' | 'C' | 'L';
  tipo: string;
  nombre: string;
  origen?: 'jne_oficial' | 'op_interno';
  es_documento_declara: boolean;
  resolucion_jne?: string | null;
  archivo_nombre: string | null;
  archivo_url: string | null;
  archivo_tipo: string | null;
  archivo_tamano_bytes: number | null;
  fecha_carga: string | null;
  estado: 'pendiente' | 'recibido' | 'aprobado_plt' | 'rechazado_plt';
  aplica: boolean;
  notas_revision: string | null;
}

const ESTADO_BADGE: Record<string, { cls: string; label: string }> = {
  pendiente: { cls: 'bg-gray-100 text-gray-600', label: '⏳ Pendiente' },
  recibido: { cls: 'bg-yellow-100 text-yellow-800', label: '🟡 Recibido' },
  aprobado_plt: { cls: 'bg-green-100 text-green-700', label: '✅ Aprobado' },
  rechazado_plt: { cls: 'bg-red-100 text-red-700', label: '❌ Rechazado' },
};

const BLOQUE_TITLE: Record<string, string> = {
  A: 'Bloque A — Obligatorios',
  B: 'Bloque B — Condicionales (aplican según situación del candidato)',
  C: 'Bloque C — Internos del proceso',
};

function formatSize(bytes: number | null): string {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
}

export function DocumentosTab({ candidatoId, dni, rol }: { candidatoId: number; dni: string | null; rol: string }) {
  const qc = useQueryClient();
  const fileInputRefs = useRef<Record<string, HTMLInputElement | null>>({});
  const [uploadingCodigo, setUploadingCodigo] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canEdit = rol === 'PLT' || rol === 'PLA' || rol === 'ADM' || rol === 'ASI';
  const canApprove = rol === 'PLT' || rol === 'PLA' || rol === 'ADM';

  const { data: docs, isLoading } = useQuery<Doc[]>({
    queryKey: ['docs', candidatoId],
    queryFn: async () => (await api.get<Doc[]>(`/candidatos/${candidatoId}/documentos`)).data,
  });

  const uploadMut = useMutation({
    mutationFn: async ({ codigo, file }: { codigo: string; file: File }) => {
      const fd = new FormData();
      fd.append('file', file);
      setUploadingCodigo(codigo);
      try {
        const { data } = await api.post<Doc>(
          `/candidatos/${candidatoId}/documentos/${codigo}/upload`,
          fd,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
        return data;
      } finally {
        setUploadingCodigo(null);
      }
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['docs', candidatoId] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al subir'),
  });

  const aprobarMut = useMutation({
    mutationFn: async (docId: number) => (await api.post<Doc>(`/documentos/${docId}/aprobar`)).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['docs', candidatoId] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al aprobar'),
  });

  const rechazarMut = useMutation({
    mutationFn: async ({ docId, motivo }: { docId: number; motivo: string }) =>
      (await api.post<Doc>(`/documentos/${docId}/rechazar`, { motivo })).data,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['docs', candidatoId] }),
    onError: (e: any) => setError(e?.response?.data?.detail || 'Error al rechazar'),
  });

  if (isLoading || !docs) return <div className="text-sm text-gray-500 p-4">Cargando documentos…</div>;

  const aplican = docs.filter((d) => d.aplica);
  const noAplican = docs.filter((d) => !d.aplica);
  const totales = {
    aprobados: aplican.filter((d) => d.estado === 'aprobado_plt').length,
    recibidos: aplican.filter((d) => d.estado === 'recibido').length,
    pendientes: aplican.filter((d) => d.estado === 'pendiente').length,
    rechazados: aplican.filter((d) => d.estado === 'rechazado_plt').length,
    total_aplica: aplican.length,
  };

  return (
    <section className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
        <div className="border rounded p-2 bg-gray-50 text-center">
          <div className="text-xl font-bold">{totales.total_aplica}</div>
          <div className="text-[10px] text-gray-600">DOCS APLICAN</div>
        </div>
        <div className="border rounded p-2 bg-green-50 text-center">
          <div className="text-xl font-bold text-green-700">{totales.aprobados}</div>
          <div className="text-[10px] text-gray-600">APROBADOS</div>
        </div>
        <div className="border rounded p-2 bg-yellow-50 text-center">
          <div className="text-xl font-bold text-yellow-800">{totales.recibidos}</div>
          <div className="text-[10px] text-gray-600">POR REVISAR</div>
        </div>
        <div className="border rounded p-2 bg-gray-100 text-center">
          <div className="text-xl font-bold text-gray-700">{totales.pendientes}</div>
          <div className="text-[10px] text-gray-600">PENDIENTES</div>
        </div>
        <div className="border rounded p-2 bg-red-50 text-center">
          <div className="text-xl font-bold text-red-700">{totales.rechazados}</div>
          <div className="text-[10px] text-gray-600">RECHAZADOS</div>
        </div>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-xs rounded p-2 flex justify-between">
          <span>{error}</span>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {!dni && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded p-3">
          🟡 Candidato sin DNI asignado (designado pendiente). No se pueden subir documentos
          hasta que el CEN entregue el acta y se asigne el DNI.
        </div>
      )}

      {(['A', 'B', 'C'] as const).map((bloque) => {
        const items = aplican.filter((d) => d.bloque === bloque).sort((a, b) => a.codigo.localeCompare(b.codigo));
        if (items.length === 0 && bloque === 'B') {
          return (
            <div key={bloque} className="bg-white border rounded p-3 text-xs text-gray-500">
              <strong>{BLOQUE_TITLE[bloque]}</strong> · ningún documento condicional aplica al candidato actualmente.
            </div>
          );
        }
        if (items.length === 0) return null;
        return (
          <div key={bloque} className="bg-white border rounded">
            <div className="px-3 py-2 border-b bg-gray-50 text-xs font-semibold uppercase text-gray-700">
              {BLOQUE_TITLE[bloque]} ({items.length})
            </div>
            <table className="w-full text-sm">
              <thead className="bg-gray-50 text-xs">
                <tr>
                  <th className="px-3 py-2 text-left w-14">Cód</th>
                  <th className="px-3 py-2 text-left">Documento</th>
                  <th className="px-3 py-2 text-left">Estado</th>
                  <th className="px-3 py-2 text-left">Archivo</th>
                  <th className="px-3 py-2 text-right">Acciones</th>
                </tr>
              </thead>
              <tbody>
                {items.map((d) => {
                  const eb = ESTADO_BADGE[d.estado];
                  const isUploadingThis = uploadingCodigo === d.codigo;
                  return (
                    <tr key={d.id} className="border-t">
                      <td className="px-3 py-2 font-mono text-xs">{d.codigo}</td>
                      <td className="px-3 py-2 text-xs">
                        <div className="flex items-center gap-1 flex-wrap">
                          <span>{d.nombre}</span>
                          {d.origen === 'jne_oficial' && (
                            <span className="text-[9px] bg-blue-100 text-blue-800 px-1.5 py-0.5 rounded font-semibold">OFICIAL JNE</span>
                          )}
                          {d.origen === 'op_interno' && (
                            <span className="text-[9px] bg-gray-200 text-gray-700 px-1.5 py-0.5 rounded">INTERNO ARPP</span>
                          )}
                          {d.es_documento_declara && <span className="text-[9px] text-blue-600">🔵 DECLARA+</span>}
                          {d.resolucion_jne && (
                            <Link to={`/normativa?q=${encodeURIComponent(d.resolucion_jne)}`}
                                  className="text-[9px] text-arpp-primary hover:underline"
                                  title="Ver resolución JNE">
                              📜 Res. {d.resolucion_jne}
                            </Link>
                          )}
                        </div>
                        {d.notas_revision && <div className="text-[10px] text-red-700 mt-0.5">⚠ {d.notas_revision}</div>}
                      </td>
                      <td className="px-3 py-2">
                        <span className={`text-[11px] px-2 py-0.5 rounded ${eb.cls}`}>{eb.label}</span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {d.archivo_nombre ? (
                          <div className="flex items-center gap-1">
                            <FileText size={12} className="text-gray-500" />
                            <span className="truncate max-w-[200px]">{d.archivo_nombre}</span>
                            <span className="text-gray-400">({formatSize(d.archivo_tamano_bytes)})</span>
                          </div>
                        ) : (
                          <span className="text-gray-400">—</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {d.archivo_url && (
                            <a
                              href={d.archivo_url}
                              target="_blank"
                              rel="noopener"
                              className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                              title="Descargar"
                            >
                              <Download size={12} />
                            </a>
                          )}
                          {canEdit && dni && (
                            <>
                              <input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                ref={(el) => (fileInputRefs.current[`u_${d.codigo}`] = el)}
                                className="hidden"
                                onChange={(e) => {
                                  const f = e.target.files?.[0];
                                  if (f) uploadMut.mutate({ codigo: d.codigo, file: f });
                                  e.target.value = '';
                                }}
                              />
                              <button
                                onClick={() => fileInputRefs.current[`u_${d.codigo}`]?.click()}
                                disabled={isUploadingThis}
                                className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                                title="Subir / Reemplazar"
                              >
                                <Upload size={12} /> {isUploadingThis ? '...' : (d.archivo_url ? 'Cambiar' : 'Subir')}
                              </button>
                              {(d.codigo === 'D01' || d.codigo === 'D02' || d.codigo === 'D12') && (
                                <>
                                  <input
                                    type="file"
                                    accept="image/*"
                                    {...({ capture: 'environment' } as any)}
                                    ref={(el) => (fileInputRefs.current[`c_${d.codigo}`] = el)}
                                    className="hidden"
                                    onChange={(e) => {
                                      const f = e.target.files?.[0];
                                      if (f) uploadMut.mutate({ codigo: d.codigo, file: f });
                                      e.target.value = '';
                                    }}
                                  />
                                  <button
                                    onClick={() => fileInputRefs.current[`c_${d.codigo}`]?.click()}
                                    disabled={isUploadingThis}
                                    className="text-arpp-primary hover:bg-gray-100 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                                    title="Tomar foto"
                                  >
                                    <Camera size={12} /> Foto
                                  </button>
                                </>
                              )}
                            </>
                          )}
                          {canApprove && d.estado === 'recibido' && (
                            <>
                              <button
                                onClick={() => aprobarMut.mutate(d.id)}
                                className="text-green-700 hover:bg-green-50 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                                title="Aprobar"
                              >
                                <Check size={12} /> Aprobar
                              </button>
                              <button
                                onClick={() => {
                                  const motivo = prompt('Motivo del rechazo:');
                                  if (motivo) rechazarMut.mutate({ docId: d.id, motivo });
                                }}
                                className="text-red-700 hover:bg-red-50 px-2 py-1 rounded text-[11px] inline-flex items-center gap-1"
                                title="Rechazar"
                              >
                                <X size={12} /> Rechazar
                              </button>
                            </>
                          )}
                          {canApprove && d.estado === 'aprobado_plt' && (
                            <button
                              onClick={() => {
                                const motivo = prompt('Motivo del rechazo (revertirá la aprobación):');
                                if (motivo) rechazarMut.mutate({ docId: d.id, motivo });
                              }}
                              className="text-red-700 hover:bg-red-50 px-2 py-1 rounded text-[11px]"
                            >
                              Rechazar
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        );
      })}

      {noAplican.length > 0 && (
        <details className="bg-white border rounded text-xs">
          <summary className="px-3 py-2 cursor-pointer text-gray-600 hover:bg-gray-50">
            {noAplican.length} documentos del Bloque B que NO aplican al candidato (clic para ver)
          </summary>
          <table className="w-full">
            <tbody>
              {noAplican.map((d) => (
                <tr key={d.id} className="border-t opacity-60">
                  <td className="px-3 py-1 font-mono w-14">{d.codigo}</td>
                  <td className="px-3 py-1">{d.nombre}</td>
                  <td className="px-3 py-1 text-gray-400 text-right">No aplica</td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}
